// Buy Box versions (Buy Box › Agents): saved criteria sets V1, V2, … that
// agents run. V1 is the account's Buy Box Settings (edits sync both ways).
// Versions hold criteria only — underwriting assumptions stay account-wide.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { Copy, Layers, Pencil, Plus, Trash2 } from "lucide-react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { buyBoxVersionLabel } from "@shared/buy-box-agents";
import { BUY_BOX_REQUIRED_CRITERIA_FIELDS } from "@shared/buy-box-setup";
import { AGENTS_KEY, VERSIONS_KEY, type VersionWithUsage, type VersionsResponse } from "./buy-box-agents-card";

const NUMBER_FIELDS = [
  { key: "maxPurchasePrice", label: "Max purchase price ($)" },
  { key: "minBedrooms", label: "Min converted bedrooms" },
  { key: "maxBedrooms", label: "Max converted bedrooms" },
  { key: "minSqft", label: "Min sqft" },
  { key: "maxSqft", label: "Max sqft" },
  { key: "minLotSize", label: "Min lot size (sqft, optional)" },
  { key: "minCocReturn", label: "Min cash-on-cash (%)" },
  { key: "minMonthlyCashFlow", label: "Min monthly cash flow ($)" },
] as const;

type NumberKey = typeof NUMBER_FIELDS[number]["key"];
type Draft = { label: string; noHoa: boolean } & Record<NumberKey, string>;

function toDraft(v?: VersionWithUsage): Draft {
  const out: Record<string, string | boolean> = { label: v?.label ?? "", noHoa: v?.noHoa === true };
  for (const f of NUMBER_FIELDS) {
    const raw = v?.[f.key];
    out[f.key] = raw == null ? "" : String(Number(raw));
  }
  return out as Draft;
}

function toCriteria(d: Draft) {
  const criteria: Record<string, number | boolean | null> = { noHoa: d.noHoa };
  for (const f of NUMBER_FIELDS) criteria[f.key] = d[f.key].trim() === "" ? null : Number(d[f.key]);
  return criteria;
}

const LABEL_BY_KEY = new Map(BUY_BOX_REQUIRED_CRITERIA_FIELDS.map((f) => [f.key, f.label]));

export function describeVersion(v: VersionWithUsage): string {
  const parts: string[] = [];
  if (v.maxPurchasePrice != null) parts.push(`up to $${Number(v.maxPurchasePrice).toLocaleString()}`);
  if (v.minBedrooms != null || v.maxBedrooms != null) parts.push(`${v.minBedrooms ?? "?"}–${v.maxBedrooms ?? "?"} bd`);
  if (v.minSqft != null || v.maxSqft != null) parts.push(`${v.minSqft ?? "?"}–${v.maxSqft ?? "?"} sqft`);
  if (v.minLotSize != null) parts.push(`${Number(v.minLotSize).toLocaleString()}+ sqft lot`);
  if (v.minCocReturn != null) parts.push(`${Number(v.minCocReturn)}%+ CoC`);
  if (v.minMonthlyCashFlow != null) parts.push(`$${Number(v.minMonthlyCashFlow).toLocaleString()}+/mo`);
  if (v.noHoa) parts.push("No HOA");
  return parts.length ? parts.join(" · ") : "No criteria yet";
}

function VersionForm({ initial, isV1, pending, onCancel, onSubmit, testIdPrefix }: {
  initial: Draft;
  isV1: boolean;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (d: Draft) => void;
  testIdPrefix: string;
}) {
  const [draft, setDraft] = useState<Draft>(initial);
  return (
    <form
      className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg border border-gray-200 p-4 mb-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(draft);
      }}
      data-testid={`form-${testIdPrefix}`}
    >
      <label className="block text-xs text-gray-600 sm:col-span-2">
        Label (optional)
        <Input value={draft.label} maxLength={60} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} className="mt-1 h-9" placeholder="e.g. Starter homes" data-testid={`input-${testIdPrefix}-label`} />
      </label>
      {NUMBER_FIELDS.map((f) => (
        <label key={f.key} className="block text-xs text-gray-600">
          {f.label}
          <Input
            type="number"
            value={draft[f.key]}
            onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
            className="mt-1 h-9"
            data-testid={`input-${testIdPrefix}-${f.key}`}
          />
        </label>
      ))}
      <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
        <Checkbox checked={draft.noHoa} onCheckedChange={(c) => setDraft((d) => ({ ...d, noHoa: c === true }))} data-testid={`checkbox-${testIdPrefix}-no-hoa`} />
        No HOA
      </label>
      {isV1 && <p className="sm:col-span-2 text-xs text-gray-500">V1 is your Buy Box Settings. Saving here updates Buy Box Settings too.</p>}
      <div className="sm:col-span-2 flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={pending} data-testid={`button-save-${testIdPrefix}`}>Save version</Button>
      </div>
    </form>
  );
}

export function BuyBoxVersionsCard({ readOnly = false }: { readOnly?: boolean }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const q = useQuery<VersionsResponse>({ queryKey: VERSIONS_KEY });
  const versions = q.data?.versions ?? [];

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: VERSIONS_KEY });
    void qc.invalidateQueries({ queryKey: AGENTS_KEY });
    void qc.invalidateQueries({ queryKey: ["/api/app/buy-box/criteria"] });
  };
  const onError = (err: unknown) => toast({ title: "Couldn't save the version", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });

  const create = useMutation({
    mutationFn: async (d: Draft) => (await apiRequest("POST", "/api/app/buy-box/versions", { label: d.label, criteria: toCriteria(d) })).json(),
    onSuccess: () => {
      setAdding(false);
      invalidate();
    },
    onError,
  });
  const update = useMutation({
    mutationFn: async ({ id, d }: { id: number; d: Draft }) => (await apiRequest("PATCH", `/api/app/buy-box/versions/${id}`, { label: d.label, criteria: toCriteria(d) })).json(),
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
    onError,
  });
  const duplicate = useMutation({
    mutationFn: async (id: number) => (await apiRequest("POST", "/api/app/buy-box/versions", { duplicateOf: id })).json(),
    onSuccess: () => invalidate(),
    onError,
  });
  const remove = useMutation({
    mutationFn: async (id: number) => (await apiRequest("DELETE", `/api/app/buy-box/versions/${id}`)).json(),
    onSuccess: () => invalidate(),
    onError: (err: unknown) => toast({ title: "Couldn't delete the version", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6" data-testid="card-buy-box-versions" data-tour="buy-box-agents-versions">
      <div className="flex items-center gap-2 mb-2">
        <Layers className="h-5 w-5 text-blue-600" />
        <h2 className="font-semibold text-gray-900">Buy Box versions</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Save different sets of Buy Box criteria and pick one for each agent. V1 is your{" "}
        <Link href="/app/buy-box/settings"><span className="text-blue-600 hover:underline cursor-pointer">Buy Box Settings</span></Link>.
        Underwriting assumptions are shared by every version.
      </p>
      {q.isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <ul className="divide-y divide-gray-100 mb-3" data-testid="list-buy-box-versions">
          {versions.map((v) => {
            const isV1 = v.versionNumber === 1;
            const inUse = v.usedBy.length > 0;
            if (editingId === v.id) {
              return (
                <li key={v.id} className="py-3" data-testid={`row-buy-box-version-${v.id}`}>
                  <VersionForm initial={toDraft(v)} isV1={isV1} pending={update.isPending} onCancel={() => setEditingId(null)} onSubmit={(d) => update.mutate({ id: v.id, d })} testIdPrefix={`edit-version-${v.id}`} />
                </li>
              );
            }
            return (
              <li key={v.id} className="py-3 flex items-center justify-between gap-3" data-testid={`row-buy-box-version-${v.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 truncate" data-testid={`text-buy-box-version-name-${v.id}`}>
                    Buy Box {buyBoxVersionLabel(v.versionNumber, v.label)}
                    {isV1 && <span className="ml-2 text-[11px] font-normal text-gray-500">Buy Box Settings</span>}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{describeVersion(v)}</div>
                  {v.missing.length > 0 && (
                    <div className="text-xs text-amber-700" data-testid={`text-buy-box-version-missing-${v.id}`}>
                      Missing: {v.missing.map((k) => LABEL_BY_KEY.get(k) ?? k).join(", ")}
                    </div>
                  )}
                  {inUse && (
                    <div className="text-xs text-gray-400">Used by {v.usedBy.map((n) => `Agent ${n ?? "?"}`).join(", ")}</div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" disabled={readOnly} onClick={() => setEditingId(v.id)} aria-label={`Edit V${v.versionNumber}`} data-testid={`button-edit-buy-box-version-${v.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" disabled={readOnly || duplicate.isPending} onClick={() => duplicate.mutate(v.id)} aria-label={`Duplicate V${v.versionNumber}`} data-testid={`button-duplicate-buy-box-version-${v.id}`}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={readOnly || isV1 || inUse || remove.isPending}
                    onClick={() => remove.mutate(v.id)}
                    title={isV1 ? "V1 is your Buy Box Settings and can't be deleted" : inUse ? "Switch the agents using this version first" : undefined}
                    aria-label={`Delete V${v.versionNumber}`}
                    data-testid={`button-delete-buy-box-version-${v.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {adding ? (
        <VersionForm initial={toDraft()} isV1={false} pending={create.isPending} onCancel={() => setAdding(false)} onSubmit={(d) => create.mutate(d)} testIdPrefix="new-version" />
      ) : (
        <Button variant="outline" size="sm" disabled={readOnly || q.isLoading} onClick={() => setAdding(true)} data-testid="button-add-buy-box-version">
          <Plus className="h-4 w-4 mr-1" /> New version
        </Button>
      )}
    </div>
  );
}
